import { useState } from 'react';
import type {
  DuplicateAnswer,
  ImportRow,
  ImportShape,
  SetRowBody,
  SubjectStatus,
} from '@ni/shared';
import {
  IMPORT_COPY_NO_TEXT,
  IMPORT_COPY_SET_ASIDE,
  IMPORT_DUPLICATE_ANSWERED_DIFFERENT,
  IMPORT_DUPLICATE_ANSWERED_SAME,
  IMPORT_DUPLICATE_DIFFERENT,
  IMPORT_DUPLICATE_SAME,
  IMPORT_DUPLICATE_TEXT_DIFFERENT,
  IMPORT_DUPLICATE_TEXT_SAME,
  IMPORT_EXCERPT_LESS,
  IMPORT_EXCERPT_MORE,
  importExcerptToggleLabel,
  importGroupEntriesLine,
  importRowNotesLine,
} from '../../lib/copy';
import { CollapsedBin } from '../ui/collapsed-bin';
import { RowLevelSelect, RowStatusSelect } from './row-controls';

/**
 * **The copies of one name, as one card** (import note §1.5).
 *
 * The question the file asks about a repeated name is a question about *the
 * group*, and it was being asked one row at a time: a row reading `Preman
 * Pendton · the same name arrived twice` with "Same person" under it, while the
 * other copy was somewhere else in the list with the identical line on it. There
 * was no screen on which the two could be read together, which is the only way
 * the question can actually be answered — the notes on each entry are the whole
 * of the evidence the file carries.
 *
 * So the copies are drawn together: one card, one heading, one line per copy
 * saying where it came from and what it says, and the two answers at the foot
 * acting on the group.
 *
 * **A copy is still a row, and carries a row's controls.** Grouping was costing
 * the office the two gestures an ordinary row has — the level it becomes and the
 * status it gets — and leaving in their place the *word* for the level, so a
 * name that happened to be printed twice could not be marked Known or given a
 * status until the group's question had been answered and the dry run re-run.
 * The controls are the row card's own ({@link RowLevelSelect}), posted for that
 * copy's locator, with the per-copy busy the card already keeps. Beside them
 * sits the one gesture that is only a copy's — **set aside**, for the entry that
 * is a stray line rather than a person, so a bad third copy can be dropped
 * without deciding anything about the other two.
 */

/** One copy of a repeated name — the row itself where the page holds it. */
export interface DuplicateCopy {
  readonly locator: string;
  readonly display_name: string;
  readonly source_label: string;
  readonly excerpt: string | null;
  /**
   * The row, where this copy is on the page. `null` where it is not: the group
   * is drawn from whichever member the page holds, and a group that straddles a
   * page boundary still shows every copy — what it cannot do is offer a gesture
   * on a row it does not have.
   */
  readonly row: ImportRow | null;
}

export interface DuplicateGroup {
  readonly kind: 'group';
  /** The survivor's locator — `ImportRow.group_key`, and the card's identity. */
  readonly key: string;
  readonly display_name: string;
  readonly copies: readonly DuplicateCopy[];
  /**
   * Whether the copies say the same thing. `null` where fewer than two of them
   * say anything at all, which is a cue nobody could read.
   */
  readonly text: 'same' | 'different' | null;
  /** The answer the office has already given, where it has given one. */
  readonly answer: DuplicateAnswer | null;
}

/** A row that is nobody's copy, drawn as the ordinary row it is. */
export interface SingleRow {
  readonly kind: 'row';
  readonly row: ImportRow;
  /** Its place in the list — the fallback name for a row whose name is empty. */
  readonly index: number;
}

export type ImportListEntry = SingleRow | DuplicateGroup;

/**
 * **The list, with each name's copies collected into one card** — pure, and the
 * whole of the grouping the tab does.
 *
 * The server has already put a group's rows next to each other and named the
 * group on every one of them (`group_key`), so this neither sorts nor searches:
 * it walks the page in the order it was given, and the first copy of a group it
 * meets is where that group's card goes. The rest are drawn from the group the
 * row carries, which holds every copy — so a card is complete even when the page
 * ends in the middle of one.
 *
 * **A group is a card while the question is open.** What makes a card is the row
 * still being filed under `duplicate_name`: once the office has answered and the
 * dry run has been re-run the reason is gone, the copies are a fold and its
 * survivor or several people who happen to share a name, and each is an ordinary
 * row again — still next to each other, because the ordering is the server's and
 * does not depend on the question.
 */
export function groupImportRows(rows: readonly ImportRow[]): ImportListEntry[] {
  const byLocator = new Map(rows.map((row) => [row.locator, row]));
  const out: ImportListEntry[] = [];
  const drawn = new Set<string>();

  rows.forEach((row, at) => {
    const group = row.preview?.duplicate_group ?? null;
    const key = row.group_key;
    if (key === null || group === null || group.length < 2 || !asksAboutCopies(row, byLocator)) {
      out.push({ kind: 'row', row, index: at + 1 });
      return;
    }
    if (drawn.has(key)) return;
    drawn.add(key);

    const copies: DuplicateCopy[] = group.map((copy) => ({
      locator: copy.locator,
      display_name: copy.display_name,
      source_label: copy.source_label,
      excerpt: copy.excerpt,
      row: byLocator.get(copy.locator) ?? null,
    }));
    out.push({
      kind: 'group',
      key,
      display_name: copies[0]?.display_name ?? row.display_name,
      copies,
      text: textCue(copies),
      answer: answerOf(copies),
    });
  });

  return out;
}

/**
 * **Is this row still asking the question?** — any copy of it on the page filed
 * under the repeat, answered or not.
 *
 * Answered-and-not-yet-re-run is deliberately still a card: the answer lands the
 * moment it is pressed and the reason goes at the next dry run, so this is the
 * window in which the office reads back what they just said.
 */
function asksAboutCopies(row: ImportRow, byLocator: ReadonlyMap<string, ImportRow>): boolean {
  const group = row.preview?.duplicate_group ?? [];
  return group.some(
    (copy) => byLocator.get(copy.locator)?.preview?.bin_reason === 'duplicate_name',
  );
}

/** Do the copies say the same thing, or different things? */
export function textCue(copies: readonly DuplicateCopy[]): 'same' | 'different' | null {
  const said = copies.map((copy) => copy.excerpt).filter((text): text is string => text !== null);
  if (said.length < 2) return null;
  return said.every((text) => text === said[0]) ? 'same' : 'different';
}

/** The answer on any copy the page holds — the server writes it on all of them. */
function answerOf(copies: readonly DuplicateCopy[]): DuplicateAnswer | null {
  for (const copy of copies) {
    const answer = copy.row?.resolution?.duplicate;
    if (answer) return answer;
  }
  return null;
}

/* ————————————————————————— the card ————————————————————————— */

export function DuplicateGroupCard({
  group,
  shape,
  vertical,
  statuses,
  editable,
  writing,
  onSet,
}: {
  group: DuplicateGroup;
  /**
   * What the parser made of the file — the word a copy's prose line uses for a
   * unit: a **bio** out of a document, a **note** out of a grid.
   */
  shape: ImportShape;
  vertical: string;
  statuses: readonly SubjectStatus[];
  editable: boolean;
  /** The locator whose write is in flight, or null — never the whole card. */
  writing: string | null;
  onSet: (locator: string, body: SetRowBody) => void;
}) {
  /* The answer is about the group, so it is sent on whichever copy the page has
     — the server answers the peers itself, which is what makes one press one
     answer rather than one press per copy. */
  const answerOn = group.copies.find((copy) => copy.row !== null)?.locator ?? null;
  const busy = writing !== null && group.copies.some((copy) => copy.locator === writing);
  const cue =
    group.text === 'same'
      ? IMPORT_DUPLICATE_TEXT_SAME
      : group.text === 'different'
        ? IMPORT_DUPLICATE_TEXT_DIFFERENT
        : null;

  return (
    <div className="import-row import-group" aria-busy={busy}>
      <div className="import-group-head">
        {/* One heading on the card: the name the copies are copies of. The
            copies underneath are lines of it, not sections of their own. */}
        <h2 className="import-group-name">{group.display_name}</h2>
        <div className="import-group-count">
          <span className="tag-faint">{importGroupEntriesLine(group.copies.length)}</span>
          {cue ? <span className="tag-faint">{cue}</span> : null}
        </div>
      </div>

      <div className="import-group-copies">
        {group.copies.map((copy) => (
          <DuplicateCopyLine
            key={copy.locator}
            copy={copy}
            shape={shape}
            vertical={vertical}
            statuses={statuses}
            editable={editable}
            busy={writing === copy.locator}
            onSet={(body) => onSet(copy.locator, body)}
          />
        ))}
      </div>

      <div className="import-group-foot">
        <div className="chips" role="group" aria-label={`Is ${group.display_name} one person?`}>
          {(
            [
              ['same', IMPORT_DUPLICATE_SAME],
              ['different', IMPORT_DUPLICATE_DIFFERENT],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={group.answer === value ? 'chip on' : 'chip'}
              aria-pressed={group.answer === value}
              disabled={!editable || busy || answerOn === null}
              onClick={() => {
                if (answerOn !== null) onSet(answerOn, { duplicate: value });
              }}
            >
              {label}
            </button>
          ))}
        </div>
        {/* What the answer already given *does*, rather than which button is
            lit: the office pressed "Same person" and what happens is that the
            later copies go onto the first one. */}
        {group.answer ? (
          <div className="src" aria-live="polite">
            {group.answer === 'same'
              ? IMPORT_DUPLICATE_ANSWERED_SAME
              : IMPORT_DUPLICATE_ANSWERED_DIFFERENT}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * One copy: where it came from, what it will be, what it says, what becomes of
 * its prose, and what it would write.
 *
 * **The controls are the row card's**, posted for this copy's locator, because a
 * copy is a row and the level and the status are the same two questions here as
 * anywhere. Three states have their own account:
 *
 * - **Folded** — the group's answer has landed and been re-run on this copy, so
 *   everything it carries is going onto the first one: the line says so and the
 *   controls are off, because there is no longer a level of its own to set.
 * - **Set aside** — the level control stays, and it is the way back: the next dry
 *   run takes a set-aside copy out of the group, and a copy that could only be
 *   dropped and never recovered is a trap.
 * - **On another page** — the group is drawn from whichever member the page holds,
 *   so a copy the page does not have is shown and not offered a gesture.
 */
function DuplicateCopyLine({
  copy,
  shape,
  vertical,
  statuses,
  editable,
  busy,
  onSet,
}: {
  copy: DuplicateCopy;
  shape: ImportShape;
  vertical: string;
  statuses: readonly SubjectStatus[];
  editable: boolean;
  busy: boolean;
  onSet: (body: SetRowBody) => void;
}) {
  const row = copy.row;
  const aside = row?.level === 'set_aside';
  const folded = row?.preview?.outcome === 'folded';
  const lines = row?.preview?.proposed ?? row?.preview?.attachments ?? null;
  /* What becomes of this copy's own prose — the same line the row card draws,
     and on a card comparing copies it is half of what tells them apart: one
     entry carrying a bio and one carrying nothing is not two of the same thing. */
  const notesLine = row?.preview ? importRowNotesLine(row.preview.notes, shape) : null;
  /* Locked rather than absent on a folded copy: the office should read what this
     entry became, and a control that can only be refused is worse than one that
     is plainly off. */
  const locked = !editable || busy || folded;

  return (
    <div className={aside ? 'import-copy is-aside' : 'import-copy'} aria-busy={busy}>
      <div className="import-copy-head">
        <span className="import-copy-where">{copy.source_label}</span>

        {row !== null ? (
          <div className="import-copy-controls">
            <RowLevelSelect
              who={copy.source_label}
              level={row.level}
              disabled={locked}
              onLevel={(level) => onSet({ level })}
            />
            {/* Only a subject has a status (§1.3), and the control follows the
                rule rather than the stored level — a copy just set to Known
                loses it on the next read, which is what the server means. */}
            {row.level === 'subject' ? (
              <RowStatusSelect
                who={copy.source_label}
                status={row.status}
                vertical={vertical}
                statuses={statuses}
                disabled={locked}
                onStatus={(status) => onSet({ status, level: 'subject' })}
              />
            ) : null}
          </div>
        ) : null}

        {folded ? <span className="tag-faint">{IMPORT_DUPLICATE_ANSWERED_SAME}</span> : null}

        {/* Offered on a copy still in play, and gone on one already dropped or
            already folded — the level control beside it says what it is, and a
            second sentence saying the same thing is a control that does
            nothing. */}
        {row !== null && editable && !aside && !folded ? (
          <button
            type="button"
            className="linkish import-copy-aside"
            disabled={busy}
            aria-label={`Set aside ${copy.source_label}`}
            onClick={() => onSet({ level: 'set_aside' })}
          >
            {IMPORT_COPY_SET_ASIDE}
          </button>
        ) : null}
      </div>

      <Excerpt text={copy.excerpt} where={copy.source_label} />

      {notesLine ? <div className="src">{notesLine}</div> : null}

      {lines && lines.length > 0 ? (
        <CollapsedBin
          label={`What this entry would write (${lines.length})`}
          openLabel="Hide what this entry would write"
        >
          {lines.map((line, index) => (
            <div className="bin-row" key={`${copy.locator}-line-${index}`}>
              <div className="bname">{line}</div>
            </div>
          ))}
        </CollapsedBin>
      ) : null}
    </div>
  );
}

/**
 * **What this copy says**, two lines of it, with the rest one press away.
 *
 * Clamped rather than cut: the office is comparing copies, and four entries each
 * showing a paragraph is a card nobody can see the shape of — but the text is
 * the evidence, so all of it has to be reachable, and by a button rather than a
 * hover.
 */
function Excerpt({ text, where }: { text: string | null; where: string }) {
  const [open, setOpen] = useState(false);
  if (text === null) return <div className="src import-copy-text">{IMPORT_COPY_NO_TEXT}</div>;

  /* Whether two lines are actually clamping anything is a measurement, and the
     card does not have one — so the toggle is offered on text long enough that
     it could be, and a one-line note is left alone rather than given a button
     that opens what is already open. */
  const clampable = text.length > EXCERPT_CLAMP_LENGTH;

  return (
    <div className="import-copy-text">
      <p className={clampable && !open ? 'import-copy-said clamped' : 'import-copy-said'}>{text}</p>
      {clampable ? (
        <button
          type="button"
          className="linkish"
          aria-expanded={open}
          aria-label={importExcerptToggleLabel(open, where)}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? IMPORT_EXCERPT_LESS : IMPORT_EXCERPT_MORE}
        </button>
      ) : null}
    </div>
  );
}

/** Longer than this and two lines are plausibly holding something back. */
const EXCERPT_CLAMP_LENGTH = 120;
